import { rawTimeZones } from '@vvo/tzdb';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { LinkButton } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { Option, Select } from 'components/atoms/select';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { useAccountProfile, useAccountTimezoneMutation } from 'queries/account';
import { getTimezoneLabelWithOffset } from 'utils/timezone';
import { SettingsLabel } from '../styles';
import { Container, Content, FormWithMarginTop } from './styled';

interface TimezoneFormValues {
  timezone: Option;
}

export const AccountTimezone = () => {
  const [form] = Form.useForm<TimezoneFormValues>();
  const translate = useTranslate();
  const [showModal, setShowModal] = useState(false);
  const { data: account } = useAccountProfile();
  const { mutate: updateTimezone, isLoading } = useAccountTimezoneMutation();
  const timezone = useMemo(
    () => getTimezoneLabelWithOffset(account?.timeZone),
    [account?.timeZone],
  );

  const options = useMemo(
    () =>
      rawTimeZones.map(({ name: _name }) => ({
        label: getTimezoneLabelWithOffset(_name),
        value: _name,
      })),
    [],
  );

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const onSubmit = async () => {
    if (!account?.accountId) {
      return;
    }
    const formValues = await form.validateFields();

    try {
      await updateTimezone({
        accountId: account?.accountId,
        timeZone: String(formValues.timezone.value),
      });

      toastService({
        title: translate('timezone.updated.success.title'),
        message: translate('timezone.updated.success.message', {
          timezone: formValues.timezone.value,
        }),
      }).success();
    } catch (e) {
      toastService({
        title: translate('timezone.updated.error.title'),
        message: translate('timezone.updated.error.message'),
      }).error();
    } finally {
      closeModal();
    }
  };

  return (
    <Container>
      <SettingsLabel>
        <FormattedMessage id="timezone" />
      </SettingsLabel>
      <Content>
        <div>{timezone}</div>
        <LinkButton onClick={openModal}>
          <FormattedMessage id="placeholder.change" />
        </LinkButton>
        <Modal
          width={464}
          title={<FormattedMessage id="timezone.select.modal.title" />}
          visible={showModal}
          onOk={onSubmit}
          onCancel={closeModal}
          okButtonProps={{ loading: isLoading }}
          okText={<FormattedMessage id="save" />}
        >
          <FormWithMarginTop
            form={form}
            layout="vertical"
            initialValues={{
              timezone: {
                label: getTimezoneLabelWithOffset(account?.timeZone),
                value: account?.timeZone,
              },
            }}
          >
            <Form.Item
              name="timezone"
              required
              label={<FormattedMessage id="timezone" />}
            >
              <Select options={options} />
            </Form.Item>
          </FormWithMarginTop>
        </Modal>
      </Content>
    </Container>
  );
};
